import React, { forwardRef } from 'react';
import cx from 'classnames';
import styled, { css } from 'styled-components';
import { withLocalePrefix } from '../../utils/translation';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';

const t = withLocalePrefix('scorecard.ratings');

type IconProps = {
  selected: boolean;
  filled?: boolean;
  interactive: boolean;
};

export type IconType =
  | 'strong_no'
  | 'no'
  | 'neutral'
  | 'yes'
  | 'strong_yes'
  | 'no_decision';

const strongNo = t('strong_no');
const no = t('no');
const neutral = t('neutral');
const yes = t('yes');
const strongYes = t('strong_yes');
const noDecision = t('no_decision');

export const StrongYesIcon = forwardRef<HTMLElement, IconProps>(
  ({ selected, interactive, filled }, ref) => {
    return (
      <Svg
        aria-hidden="true"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        className={cx({ selected, filled, strong_yes: true })}
        selectedColor={FullPalette.GREEN_E400}
        ref={ref}
        interactive={interactive}
        alt={strongYes}
        aria-label={strongYes}
      >
        <title>{strongYes}</title>
        <rect
          id="selected"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          fill={FullPalette.IRIS_E50}
        />
        <path
          id="filled"
          d="M13.652 30.2841C13.364 30.2841 13.076 30.1881 12.824 30.0081C12.32 29.6361 12.128 29.0001 12.332 28.4121L14.66 21.8841L10.364 18.6441C9.87195 18.2721 9.67995 17.6601 9.87195 17.0841C10.064 16.5081 10.592 16.1241 11.204 16.1241H16.724L18.68 10.6521C18.884 10.0881 19.4 9.72809 20 9.72809C20.6 9.72809 21.116 10.0881 21.32 10.6521L23.276 16.1241H28.808C29.42 16.1241 29.936 16.4961 30.14 17.0841C30.332 17.6601 30.14 18.2841 29.648 18.6441L25.34 21.8721L27.668 28.4001C27.872 28.9881 27.68 29.6241 27.176 29.9961C26.672 30.3681 26 30.3681 25.508 29.9841L20 25.8681L14.492 29.9961C14.252 30.1881 13.952 30.2841 13.652 30.2841Z"
          fill={FullPalette.GREEN_E400}
        />
        <path
          id="main-content"
          d="M13.652 30.2841C13.364 30.2841 13.076 30.1881 12.824 30.0081C12.32 29.6361 12.128 29.0001 12.332 28.4121L14.66 21.8841L10.364 18.6441C9.87201 18.2721 9.68001 17.6601 9.87201 17.0841C10.064 16.5081 10.592 16.1241 11.204 16.1241H16.724L18.68 10.6521C18.884 10.0881 19.4 9.72806 20 9.72806C20.6 9.72806 21.116 10.0881 21.32 10.6521L23.276 16.1241H28.808C29.42 16.1241 29.936 16.4961 30.14 17.0841C30.332 17.6601 30.14 18.2841 29.648 18.6441L25.34 21.8721L27.668 28.4001C27.872 28.9881 27.68 29.6241 27.176 29.9961C26.672 30.3681 26 30.3681 25.508 29.9841L20 25.8681L14.492 29.9961C14.252 30.1881 13.952 30.2841 13.652 30.2841ZM11.216 17.5281L15.8 20.9601C16.124 21.2001 16.256 21.6201 16.112 22.0041L13.664 28.8561L19.436 24.5241C19.76 24.2841 20.216 24.2841 20.54 24.5241L26.312 28.8561L23.864 22.0041C23.732 21.6201 23.852 21.2001 24.176 20.9601L28.76 17.5281H22.916C22.532 17.5281 22.172 17.2761 22.052 16.9161L20 11.1441L17.936 16.9161C17.804 17.2881 17.456 17.5281 17.072 17.5281H11.216Z"
          fill={FullPalette.EVERGREEN_700}
        />
        <rect
          id="focused"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          stroke={FullPalette.EVERGREEN_700}
          strokeWidth="1.5"
        />
      </Svg>
    );
  }
);

export const YesIcon = forwardRef<HTMLElement, IconProps>(
  ({ selected, interactive, filled }, ref) => {
    return (
      <Svg
        aria-hidden="true"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        className={cx({ selected, filled, yes: true })}
        selectedColor={FullPalette.GREEN_E200}
        ref={ref}
        interactive={interactive}
        alt={yes}
        aria-label={yes}
      >
        <title>{yes}</title>
        <rect
          id="selected"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          fill={FullPalette.IRIS_E50}
        />
        <path
          id="filled"
          d="M15 18L15.5 19.5L17.5 18.5L19.5 17L20.5 11.5H23V16.5H29V28.5H21L19 27H16L15 28.5H10.5V18H15Z"
          fill={FullPalette.GREEN_E200}
        />
        <path
          id="main-content"
          d="M30.164 20.8881C30.164 20.3841 29.996 19.9041 29.684 19.4961C29.612 19.4001 29.612 19.2561 29.684 19.1601C29.996 18.7641 30.164 18.2841 30.164 17.7681C30.164 16.5201 29.156 15.5121 27.908 15.5121H25.676H23.756C23.72 15.5121 23.684 15.4761 23.684 15.4401V12.8841C23.684 11.6361 22.676 10.6281 21.428 10.6281C20.18 10.6281 19.172 11.6361 19.172 12.8841V14.6601C19.16 16.6041 17.744 18.2241 15.896 18.5601V18.5481C15.896 17.7321 15.236 17.0721 14.42 17.0721H11.312C10.496 17.0721 9.836 17.7321 9.836 18.5481V27.8841C9.836 28.7001 10.496 29.3601 11.312 29.3601H14.408C15.224 29.3601 15.884 28.7001 15.884 27.8841V27.8121H18.296C18.5 27.8121 18.908 28.1241 19.232 28.3761C19.844 28.8441 20.54 29.3721 21.416 29.3721H25.676H27.908C29.156 29.3721 30.164 28.3641 30.164 27.1161C30.164 26.6121 29.996 26.1321 29.684 25.7241C29.612 25.6281 29.612 25.4841 29.684 25.3881C29.996 24.9921 30.164 24.5121 30.164 23.9961C30.164 23.4921 29.996 23.0121 29.684 22.6041C29.612 22.5081 29.612 22.3641 29.684 22.2681C30.008 21.8721 30.164 21.3921 30.164 20.8881ZM14.492 27.7401C14.492 27.8601 14.396 27.9561 14.276 27.9561H11.456C11.336 27.9561 11.24 27.8601 11.24 27.7401V18.6921C11.24 18.5721 11.336 18.4761 11.456 18.4761H14.264C14.384 18.4761 14.48 18.5721 14.48 18.6921V27.7401H14.492ZM28.58 20.3601C28.7 20.5161 28.76 20.6961 28.76 20.8881C28.76 21.0801 28.7 21.2601 28.58 21.4161C28.112 22.0281 28.112 22.8681 28.58 23.4801C28.7 23.6361 28.76 23.8161 28.76 24.0081C28.76 24.2001 28.7 24.3801 28.58 24.5361C28.112 25.1481 28.112 25.9881 28.58 26.6001C28.7 26.7561 28.76 26.9361 28.76 27.1281C28.76 27.5961 28.376 27.9801 27.908 27.9801H25.676H21.416C21.02 27.9801 20.552 27.6201 20.096 27.2721C19.544 26.8521 18.98 26.4201 18.308 26.4201H16.112C15.992 26.4201 15.896 26.3241 15.896 26.2041V20.1921C15.896 20.0841 15.968 19.9881 16.076 19.9761C18.62 19.5561 20.564 17.3361 20.564 14.6721V12.9081C20.564 12.4641 20.888 12.0801 21.332 12.0321C21.836 11.9841 22.268 12.3801 22.268 12.8841V15.4401C22.268 16.2561 22.928 16.9161 23.744 16.9161H25.664H27.896C28.364 16.9161 28.748 17.3001 28.748 17.7681C28.748 17.9601 28.688 18.1401 28.568 18.2961C28.112 18.9081 28.112 19.7481 28.58 20.3601Z"
          fill={FullPalette.EVERGREEN_700}
        />
        <rect
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          strokeWidth="1.5"
          stroke={FullPalette.EVERGREEN_700}
          id="focused"
        />
      </Svg>
    );
  }
);

export const NeutralIcon = forwardRef<HTMLElement, IconProps>(
  ({ selected, interactive, filled }, ref) => {
    return (
      <Svg
        aria-hidden="true"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        className={cx({ selected, filled, neutral: true })}
        selectedColor={FullPalette.MARIGOLD_E200}
        ref={ref}
        interactive={interactive}
        alt={neutral}
        aria-label={neutral}
      >
        <title>{neutral}</title>
        <rect
          id="selected"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          fill={FullPalette.IRIS_E50}
        />
        <path
          id="filled"
          d="M20.0001 30.776C14.0601 30.776 9.22412 25.94 9.22412 20C9.22412 14.06 14.0601 9.224 20.0001 9.224C25.9401 9.224 30.7761 14.06 30.7761 20C30.7761 25.94 25.9401 30.776 20.0001 30.776Z"
          fill={FullPalette.MARIGOLD_E200}
        />
        <path
          id="main-content"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.224 20C9.224 25.94 14.06 30.776 20 30.776C25.94 30.776 30.776 25.94 30.776 20C30.776 14.06 25.94 9.224 20 9.224C14.06 9.224 9.224 14.06 9.224 20ZM10.628 20C10.628 14.828 14.828 10.628 20 10.628C25.172 10.628 29.372 14.828 29.372 20C29.372 25.172 25.172 29.372 20 29.372C14.828 29.372 10.628 25.172 10.628 20ZM15.356 20.696H24.656C25.04 20.696 25.352 20.384 25.352 20C25.352 19.616 25.04 19.304 24.656 19.304H15.356C14.972 19.304 14.66 19.616 14.66 20C14.66 20.384 14.972 20.696 15.356 20.696Z"
          fill={FullPalette.EVERGREEN_700}
        />
        <rect
          id="focused"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          stroke={FullPalette.EVERGREEN_700}
          strokeWidth="1.5"
        />
      </Svg>
    );
  }
);

export const NoIcon = forwardRef<HTMLElement, IconProps>(
  ({ selected, interactive, filled }, ref) => {
    return (
      <Svg
        aria-hidden="true"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        className={cx({ selected, filled, no: true })}
        selectedColor={FullPalette.POPPY_E100}
        ref={ref}
        interactive={interactive}
        alt={no}
        aria-label={no}
      >
        <title>{no}</title>
        <rect
          id="selected"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          fill={FullPalette.IRIS_E50}
        />
        <path
          id="filled"
          d="M15 22L15.5 20.5L17.5 21.5L19.5 23L20.5 28.5H23V23.5H29V11.5H21L19 13H16L15 11.5H10.5V22H15Z"
          fill={FullPalette.POPPY_E100}
        />
        <path
          id="main-content"
          d="M29.696 17.72C29.624 17.624 29.624 17.48 29.696 17.384C30.008 16.988 30.176 16.508 30.176 15.992C30.176 15.488 30.008 15.008 29.696 14.6C29.624 14.504 29.624 14.36 29.696 14.264C30.008 13.868 30.176 13.388 30.176 12.872C30.176 11.624 29.168 10.616 27.92 10.616H25.688H21.428C20.552 10.616 19.856 11.144 19.244 11.612C18.92 11.864 18.512 12.176 18.308 12.176H15.896V12.104C15.896 11.288 15.236 10.628 14.42 10.628H11.312C10.496 10.628 9.836 11.288 9.836 12.104V21.44C9.836 22.256 10.496 22.916 11.312 22.916H14.408C15.224 22.916 15.884 22.256 15.884 21.44C17.732 21.776 19.148 23.396 19.148 25.34V27.116C19.148 28.364 20.156 29.372 21.404 29.372C22.652 29.372 23.66 28.364 23.66 27.116V24.56C23.66 24.524 23.696 24.488 23.732 24.488H25.652H27.884C29.132 24.488 30.14 23.48 30.14 22.232C30.14 21.728 29.972 21.248 29.66 20.84C29.588 20.744 29.588 20.6 29.66 20.504C29.972 20.108 30.14 19.628 30.14 19.112C30.14 18.596 30.008 18.128 29.696 17.72ZM14.492 21.308C14.492 21.428 14.396 21.524 14.276 21.524H11.456C11.336 21.524 11.24 21.428 11.24 21.308V12.26C11.24 12.14 11.336 12.044 11.456 12.044H14.264C14.384 12.044 14.48 12.14 14.48 12.26V21.308H14.492ZM28.58 21.704C28.7 21.86 28.76 22.04 28.76 22.232C28.76 22.7 28.376 23.084 27.908 23.084H25.676H23.756C22.94 23.084 22.28 23.744 22.28 24.56V27.116C22.28 27.608 21.848 28.016 21.344 27.968C20.9 27.932 20.576 27.536 20.576 27.092V25.34C20.576 22.676 18.632 20.468 16.088 20.036C15.98 20.024 15.908 19.928 15.908 19.82V13.808C15.908 13.688 16.004 13.592 16.124 13.592H18.308C18.98 13.592 19.544 13.16 20.096 12.74C20.552 12.392 21.02 12.032 21.416 12.032H25.676H27.908C28.376 12.032 28.76 12.416 28.76 12.884C28.76 13.076 28.7 13.256 28.58 13.412C28.112 14.024 28.112 14.864 28.58 15.476C28.7 15.632 28.76 15.812 28.76 16.004C28.76 16.196 28.7 16.376 28.58 16.532C28.112 17.144 28.112 17.984 28.58 18.596C28.7 18.752 28.76 18.932 28.76 19.124C28.76 19.316 28.7 19.496 28.58 19.652C28.112 20.252 28.112 21.092 28.58 21.704Z"
          fill={FullPalette.EVERGREEN_700}
        />
        <rect
          id="focused"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          stroke={FullPalette.EVERGREEN_700}
          strokeWidth="1.5"
        />
      </Svg>
    );
  }
);

export const StrongNoIcon = forwardRef<HTMLElement, IconProps>(
  ({ selected, interactive, filled }, ref) => {
    return (
      <Svg
        aria-hidden="true"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        className={cx({ selected, filled, strong_no: true })}
        selectedColor={FullPalette.POPPY_E300}
        ref={ref}
        interactive={interactive}
        alt={strongNo}
        aria-label={strongNo}
      >
        <title>{strongNo}</title>
        <rect
          id="selected"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          fill={FullPalette.IRIS_E50}
        />
        <path
          id="filled"
          d="M20.0001 30.776C14.0601 30.776 9.22412 25.94 9.22412 20C9.22412 14.06 14.0601 9.224 20.0001 9.224C25.9401 9.224 30.7761 14.06 30.7761 20C30.7761 25.94 25.9401 30.776 20.0001 30.776Z"
          fill={FullPalette.POPPY_E300}
        />
        <path
          id="main-content"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.224 20C9.224 25.94 14.06 30.776 20 30.776C25.94 30.776 30.776 25.94 30.776 20C30.776 14.06 25.94 9.224 20 9.224C14.06 9.224 9.224 14.06 9.224 20ZM10.628 20C10.628 14.828 14.828 10.628 20 10.628C25.172 10.628 29.372 14.828 29.372 20C29.372 25.172 25.172 29.372 20 29.372C14.828 29.372 10.628 25.172 10.628 20ZM22.616 16.3881C22.88 16.1241 23.312 16.0881 23.588 16.3401C23.9 16.6161 23.912 17.0841 23.624 17.3601L21.128 19.8321C21.044 19.9161 21.044 20.0601 21.128 20.1441L23.6 22.6161C23.876 22.8921 23.876 23.3361 23.6 23.6121C23.468 23.7441 23.288 23.8161 23.108 23.8161C22.928 23.8161 22.748 23.7441 22.616 23.6121L20.144 21.1401C20.06 21.0561 19.916 21.0561 19.832 21.1401L17.36 23.6121C17.228 23.7441 17.048 23.8161 16.868 23.8161C16.688 23.8161 16.508 23.7441 16.376 23.6121C16.1 23.3361 16.1 22.8921 16.376 22.6161L18.848 20.1441C18.932 20.0601 18.932 19.9161 18.848 19.8321L16.376 17.3601C16.088 17.0721 16.1 16.6041 16.412 16.3401C16.688 16.0881 17.12 16.1241 17.384 16.3881L19.844 18.8481C19.928 18.9321 20.072 18.9321 20.156 18.8481L22.616 16.3881Z"
          fill={FullPalette.EVERGREEN_700}
        />
        <rect
          id="focused"
          x="0.75"
          y="0.75"
          width="38.5"
          height="38.5"
          rx="19.25"
          stroke={FullPalette.EVERGREEN_700}
          strokeWidth="1.5"
        />
      </Svg>
    );
  }
);

type NoPaddingIconProps = {
  title?: string;
};

const useTitle = (name: undefined | string, title: string) => {
  return [name, title].filter((s) => !!s).join(' - ');
};

export const NoPaddingStrongYesIcon = ({
  title: propsTitle,
}: NoPaddingIconProps) => {
  const title = useTitle(propsTitle, strongYes);

  return (
    <Svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      alt={strongYes}
      aria-label={strongYes}
      className="strong_yes"
    >
      <title>{title}</title>
      <path
        d="M5.65195 22.2841C5.36395 22.2841 5.07595 22.1881 4.82395 22.0081C4.31995 21.6361 4.12795 21.0001 4.33195 20.4121L6.65995 13.8841L2.36395 10.6441C1.87195 10.2721 1.67995 9.66009 1.87195 9.08409C2.06395 8.50809 2.59195 8.12409 3.20395 8.12409H8.72395L10.68 2.65209C10.884 2.08809 11.4 1.72809 12 1.72809C12.6 1.72809 13.116 2.08809 13.32 2.65209L15.276 8.12409H20.808C21.42 8.12409 21.936 8.49609 22.14 9.08409C22.332 9.66009 22.14 10.2841 21.648 10.6441L17.34 13.8721L19.668 20.4001C19.872 20.9881 19.68 21.6241 19.176 21.9961C18.672 22.3681 18 22.3681 17.508 21.9841L12 17.8681L6.49195 21.9961C6.25195 22.1881 5.95195 22.2841 5.65195 22.2841Z"
        fill={FullPalette.GREEN_E400}
      />
      <path
        d="M5.65201 22.2841C5.36401 22.2841 5.07601 22.1881 4.82401 22.0081C4.32001 21.6361 4.12801 21.0001 4.33201 20.4121L6.66001 13.8841L2.36401 10.6441C1.87201 10.2721 1.68001 9.66006 1.87201 9.08406C2.06401 8.50806 2.59201 8.12406 3.20401 8.12406H8.72401L10.68 2.65206C10.884 2.08806 11.4 1.72806 12 1.72806C12.6 1.72806 13.116 2.08806 13.32 2.65206L15.276 8.12406H20.808C21.42 8.12406 21.936 8.49606 22.14 9.08406C22.332 9.66006 22.14 10.2841 21.648 10.6441L17.34 13.8721L19.668 20.4001C19.872 20.9881 19.68 21.6241 19.176 21.9961C18.672 22.3681 18 22.3681 17.508 21.9841L12 17.8681L6.49201 21.9961C6.25201 22.1881 5.95201 22.2841 5.65201 22.2841ZM3.21601 9.52806L7.80001 12.9601C8.12401 13.2001 8.25601 13.6201 8.11201 14.0041L5.66401 20.8561L11.436 16.5241C11.76 16.2841 12.216 16.2841 12.54 16.5241L18.312 20.8561L15.864 14.0041C15.732 13.6201 15.852 13.2001 16.176 12.9601L20.76 9.52806H14.916C14.532 9.52806 14.172 9.27606 14.052 8.91606L12 3.14406L9.93601 8.91606C9.80401 9.28806 9.45601 9.52806 9.07201 9.52806H3.21601V9.52806Z"
        fill={FullPalette.EVERGREEN_700}
      />
    </Svg>
  );
};

export const NoPaddingYesIcon = ({ title: propsTitle }: NoPaddingIconProps) => {
  const title = useTitle(propsTitle, yes);

  return (
    <Svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      alt={yes}
      aria-label={yes}
      className="yes"
    >
      <title>{title}</title>
      <path
        d="M7 10L7.5 11.5L9.5 10.5L11.5 9L12.5 3.5H15V8.5H21V20.5H13L11 19H8L7 20.5H2.5V10H7Z"
        fill={FullPalette.GREEN_E200}
      />
      <path
        d="M22.1639 12.8881C22.1639 12.3841 21.9959 11.9041 21.6839 11.4961C21.6119 11.4001 21.6119 11.2561 21.6839 11.1601C21.9959 10.7641 22.1639 10.2841 22.1639 9.76811C22.1639 8.52011 21.1559 7.51211 19.9079 7.51211H17.6759H15.7559C15.7199 7.51211 15.6839 7.47611 15.6839 7.44011V4.88411C15.6839 3.63611 14.6759 2.62811 13.4279 2.62811C12.1799 2.62811 11.1719 3.63611 11.1719 4.88411V6.66011C11.1599 8.60411 9.74394 10.2241 7.89594 10.5601V10.5481C7.89594 9.73211 7.23594 9.07211 6.41994 9.07211H3.31194C2.49594 9.07211 1.83594 9.73211 1.83594 10.5481V19.8841C1.83594 20.7001 2.49594 21.3601 3.31194 21.3601H6.40794C7.22394 21.3601 7.88394 20.7001 7.88394 19.8841V19.8121H10.2959C10.4999 19.8121 10.9079 20.1241 11.2319 20.3761C11.8439 20.8441 12.5399 21.3721 13.4159 21.3721H17.6759H19.9079C21.1559 21.3721 22.1639 20.3641 22.1639 19.1161C22.1639 18.6121 21.9959 18.1321 21.6839 17.7241C21.6119 17.6281 21.6119 17.4841 21.6839 17.3881C21.9959 16.9921 22.1639 16.5121 22.1639 15.9961C22.1639 15.4921 21.9959 15.0121 21.6839 14.6041C21.6119 14.5081 21.6119 14.3641 21.6839 14.2681C22.0079 13.8721 22.1639 13.3921 22.1639 12.8881ZM6.49194 19.7401C6.49194 19.8601 6.39594 19.9561 6.27594 19.9561H3.45594C3.33594 19.9561 3.23994 19.8601 3.23994 19.7401V10.6921C3.23994 10.5721 3.33594 10.4761 3.45594 10.4761H6.26394C6.38394 10.4761 6.47994 10.5721 6.47994 10.6921V19.7401H6.49194ZM20.5799 12.3601C20.6999 12.5161 20.7599 12.6961 20.7599 12.8881C20.7599 13.0801 20.6999 13.2601 20.5799 13.4161C20.1119 14.0281 20.1119 14.8681 20.5799 15.4801C20.6999 15.6361 20.7599 15.8161 20.7599 16.0081C20.7599 16.2001 20.6999 16.3801 20.5799 16.5361C20.1119 17.1481 20.1119 17.9881 20.5799 18.6001C20.6999 18.7561 20.7599 18.9361 20.7599 19.1281C20.7599 19.5961 20.3759 19.9801 19.9079 19.9801H17.6759H13.4159C13.0199 19.9801 12.5519 19.6201 12.0959 19.2721C11.5439 18.8521 10.9799 18.4201 10.3079 18.4201H8.11194C7.99194 18.4201 7.89594 18.3241 7.89594 18.2041V12.1921C7.89594 12.0841 7.96794 11.9881 8.07594 11.9761C10.6199 11.5561 12.5639 9.33611 12.5639 6.67211V4.90811C12.5639 4.46411 12.8879 4.08011 13.3319 4.03211C13.8359 3.98411 14.2679 4.38011 14.2679 4.88411V7.44011C14.2679 8.25611 14.9279 8.91611 15.7439 8.91611H17.6639H19.8959C20.3639 8.91611 20.7479 9.30011 20.7479 9.76811C20.7479 9.96011 20.6879 10.1401 20.5679 10.2961C20.1119 10.9081 20.1119 11.7481 20.5799 12.3601Z"
        fill={FullPalette.EVERGREEN_700}
      />
    </Svg>
  );
};

export const NoPaddingNeutralIcon = ({
  title: propsTitle,
}: NoPaddingIconProps) => {
  const title = useTitle(propsTitle, neutral);

  return (
    <Svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      alt={neutral}
      aria-label={neutral}
      className="neutral"
    >
      <title>{title}</title>
      <path
        d="M12.0001 22.776C6.06012 22.776 1.22412 17.94 1.22412 12C1.22412 6.06 6.06012 1.224 12.0001 1.224C17.9401 1.224 22.7761 6.06 22.7761 12C22.7761 17.94 17.9401 22.776 12.0001 22.776Z"
        fill={FullPalette.MARIGOLD_E200}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.224 12C1.224 17.94 6.06 22.776 12 22.776C17.94 22.776 22.776 17.94 22.776 12C22.776 6.06 17.94 1.224 12 1.224C6.06 1.224 1.224 6.06 1.224 12ZM2.628 12C2.628 6.828 6.828 2.628 12 2.628C17.172 2.628 21.372 6.828 21.372 12C21.372 17.172 17.172 21.372 12 21.372C6.828 21.372 2.628 17.172 2.628 12ZM7.356 12.696H16.656C17.04 12.696 17.352 12.384 17.352 12C17.352 11.616 17.04 11.304 16.656 11.304H7.356C6.972 11.304 6.66 11.616 6.66 12C6.66 12.384 6.972 12.696 7.356 12.696Z"
        fill={FullPalette.EVERGREEN_700}
      />
    </Svg>
  );
};

export const NoPaddingNoIcon = ({ title: propsTitle }: NoPaddingIconProps) => {
  const title = useTitle(propsTitle, no);

  return (
    <Svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      alt={no}
      aria-label={no}
      className="no"
    >
      <title>{title}</title>
      <path
        d="M7 14L7.5 12.5L9.5 13.5L11.5 15L12.5 20.5H15V15.5H21V3.5H13L11 5H8L7 3.5H2.5V14H7Z"
        fill={FullPalette.POPPY_E100}
      />
      <path
        d="M21.696 9.72C21.624 9.624 21.624 9.48 21.696 9.384C22.008 8.988 22.176 8.508 22.176 7.992C22.176 7.488 22.008 7.008 21.696 6.6C21.624 6.504 21.624 6.36 21.696 6.264C22.008 5.868 22.176 5.388 22.176 4.872C22.176 3.624 21.168 2.616 19.92 2.616H17.688H13.428C12.552 2.616 11.856 3.144 11.244 3.612C10.92 3.864 10.512 4.176 10.308 4.176H7.896V4.104C7.896 3.288 7.236 2.628 6.42 2.628H3.312C2.496 2.628 1.836 3.288 1.836 4.104V13.44C1.836 14.256 2.496 14.916 3.312 14.916H6.408C7.224 14.916 7.884 14.256 7.884 13.44C9.732 13.776 11.148 15.396 11.148 17.34V19.116C11.148 20.364 12.156 21.372 13.404 21.372C14.652 21.372 15.66 20.364 15.66 19.116V16.56C15.66 16.524 15.696 16.488 15.732 16.488H17.652H19.884C21.132 16.488 22.14 15.48 22.14 14.232C22.14 13.728 21.972 13.248 21.66 12.84C21.588 12.744 21.588 12.6 21.66 12.504C21.972 12.108 22.14 11.628 22.14 11.112C22.14 10.596 22.008 10.128 21.696 9.72ZM6.492 13.308C6.492 13.428 6.396 13.524 6.276 13.524H3.456C3.336 13.524 3.24 13.428 3.24 13.308V4.26C3.24 4.14 3.336 4.044 3.456 4.044H6.264C6.384 4.044 6.48 4.14 6.48 4.26V13.308H6.492ZM20.58 13.704C20.7 13.86 20.76 14.04 20.76 14.232C20.76 14.7 20.376 15.084 19.908 15.084H17.676H15.756C14.94 15.084 14.28 15.744 14.28 16.56V19.116C14.28 19.608 13.848 20.016 13.344 19.968C12.9 19.932 12.576 19.536 12.576 19.092V17.34C12.576 14.676 10.632 12.468 8.088 12.036C7.98 12.024 7.908 11.928 7.908 11.82V5.808C7.908 5.688 8.004 5.592 8.124 5.592H10.308C10.98 5.592 11.544 5.16 12.096 4.74C12.552 4.392 13.02 4.032 13.416 4.032H17.676H19.908C20.376 4.032 20.76 4.416 20.76 4.884C20.76 5.076 20.7 5.256 20.58 5.412C20.112 6.024 20.112 6.864 20.58 7.476C20.7 7.632 20.76 7.812 20.76 8.004C20.76 8.196 20.7 8.376 20.58 8.532C20.112 9.144 20.112 9.984 20.58 10.596C20.7 10.752 20.76 10.932 20.76 11.124C20.76 11.316 20.7 11.496 20.58 11.652C20.112 12.252 20.112 13.092 20.58 13.704Z"
        fill={FullPalette.EVERGREEN_700}
      />
    </Svg>
  );
};

export const NoPaddingStrongNoIcon = ({
  title: propsTitle,
}: NoPaddingIconProps) => {
  const title = useTitle(propsTitle, strongNo);
  return (
    <Svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      alt={strongNo}
      aria-label={strongNo}
      className="strong_no"
    >
      <title>{title}</title>
      <path
        d="M12.0001 22.776C6.06012 22.776 1.22412 17.94 1.22412 12C1.22412 6.06 6.06012 1.224 12.0001 1.224C17.9401 1.224 22.7761 6.06 22.7761 12C22.7761 17.94 17.9401 22.776 12.0001 22.776Z"
        fill={FullPalette.POPPY_E300}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.224 12C1.224 17.94 6.06 22.776 12 22.776C17.94 22.776 22.776 17.94 22.776 12C22.776 6.06 17.94 1.224 12 1.224C6.06 1.224 1.224 6.06 1.224 12ZM2.628 12C2.628 6.828 6.828 2.628 12 2.628C17.172 2.628 21.372 6.828 21.372 12C21.372 17.172 17.172 21.372 12 21.372C6.828 21.372 2.628 17.172 2.628 12ZM14.616 8.38805C14.88 8.12405 15.312 8.08805 15.588 8.34005C15.9 8.61605 15.912 9.08405 15.624 9.36005L13.128 11.8321C13.044 11.9161 13.044 12.0601 13.128 12.1441L15.6 14.6161C15.876 14.8921 15.876 15.3361 15.6 15.6121C15.468 15.7441 15.288 15.8161 15.108 15.8161C14.928 15.8161 14.748 15.7441 14.616 15.6121L12.144 13.1401C12.06 13.0561 11.916 13.0561 11.832 13.1401L9.36 15.6121C9.228 15.7441 9.048 15.8161 8.868 15.8161C8.688 15.8161 8.508 15.7441 8.376 15.6121C8.1 15.3361 8.1 14.8921 8.376 14.6161L10.848 12.1441C10.932 12.0601 10.932 11.9161 10.848 11.8321L8.376 9.36005C8.088 9.07205 8.1 8.60405 8.412 8.34005C8.688 8.08805 9.12 8.12405 9.384 8.38805L11.844 10.8481C11.928 10.9321 12.072 10.9321 12.156 10.8481L14.616 8.38805Z"
        fill={FullPalette.EVERGREEN_700}
      />
    </Svg>
  );
};

export const NoPaddingNoDecisionIcon = ({
  title: propsTitle,
}: NoPaddingIconProps) => {
  const title = useTitle(propsTitle, noDecision);

  return (
    <Svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      alt={noDecision}
      aria-label={noDecision}
      className="no-decision"
    >
      <title>{title}</title>
      <path
        d="M2.62805 12C2.62805 17.172 6.82805 21.372 12.0001 21.372C17.1721 21.372 21.3721 17.172 21.3721 12C21.3721 6.82799 17.1721 2.62799 12.0001 2.62799C6.82805 2.62799 2.62805 6.82799 2.62805 12Z"
        fill={FullPalette.GREY_600}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1.224C6.06 1.224 1.224 6.06 1.224 12C1.224 17.94 6.06 22.776 12 22.776C17.94 22.776 22.776 17.94 22.776 12C22.776 6.06 17.94 1.224 12 1.224ZM12 21.372C6.828 21.372 2.628 17.172 2.628 12C2.628 6.828 6.828 2.628 12 2.628C17.172 2.628 21.372 6.828 21.372 12C21.372 17.172 17.172 21.372 12 21.372ZM12 18.2041C12.5104 18.2041 12.924 17.7905 12.924 17.2801C12.924 16.7698 12.5104 16.3561 12 16.3561C11.4897 16.3561 11.076 16.7698 11.076 17.2801C11.076 17.7905 11.4897 18.2041 12 18.2041ZM8.29196 8.04009C8.71196 6.26409 10.368 4.96809 12.3 5.11209C14.172 5.25609 15.684 6.79209 15.792 8.66409C15.9 10.5601 14.616 12.1801 12.864 12.5881C12.768 12.6121 12.696 12.7081 12.696 12.8041V14.4001C12.696 14.7841 12.384 15.0961 12 15.0961H11.988C11.604 15.0961 11.292 14.7841 11.292 14.4001V11.9401C11.292 11.5801 11.58 11.2921 11.94 11.2921C13.236 11.2921 14.364 10.2481 14.388 8.95209C14.412 7.59609 13.332 6.49209 11.988 6.49209C10.848 6.49209 9.88796 7.28409 9.64796 8.35209C9.57596 8.67609 9.28796 8.89209 8.96396 8.89209C8.51996 8.89209 8.19596 8.47209 8.29196 8.04009Z"
        fill={FullPalette.EVERGREEN_700}
      />
    </Svg>
  );
};

const nonInteractiveCss = css`
  #selected {
    visibility: hidden;
  }

  #focused {
    visibility: hidden;
  }

  #filled {
    visibility: hidden;
  }

  &.selected {
    #selected {
      visibility: visible;
      ${/* @ts-expect-error */ ''}
      fill: ${({ selectedColor }) => selectedColor};
    }

    #focused {
      visibility: visible;
    }

    #main-content {
      fill: ${FullPalette.EVERGREEN_700};
    }
  }

  &.filled {
    #filled {
      visibility: visible;
      ${/* @ts-expect-error */ ''}
      fill: ${({ selectedColor }) => selectedColor};
    }

    #main-content {
      fill: ${FullPalette.EVERGREEN_700};
    }
  }
`;

const interactiveCss = css`
  cursor: pointer;

  #main-content {
    fill: ${FullPalette.EVERGREEN_E300};
  }

  #selected {
    visibility: hidden;
  }

  #focused {
    visibility: hidden;
  }

  &:hover {
    #selected {
      visibility: visible;
    }
  }

  &:focus {
    #focused {
      visibility: visible;
    }

    #main-content {
      fill: ${FullPalette.EVERGREEN_700};
    }
  }

  ${nonInteractiveCss}
`;

export const containerCss = css`
  outline: none;

  ${(props: any) => {
    const { interactive } = props;

    if (!interactive) {
      return nonInteractiveCss;
    }

    return interactiveCss;
  }};
`;

// flow sucks
const Svg: any = styled.svg`
  ${containerCss}
`;
